import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import SidebarLayout from '../components/SidebarLayout';
import { useAnalyticsSummary } from '../hooks/useBilling';
import { apiClient } from '../lib/api';

/* ─── helpers ─── */
function fmtCost(n) {
  if (n == null || isNaN(n)) return '0.00';
  const v = Number(n);
  if (v === 0)      return '0.00';
  if (v < 0.0001)   return v.toFixed(6);
  if (v < 0.01)     return v.toFixed(4);
  return v.toFixed(2);
}
// Keep fmt2 as alias for non-cost uses (none currently, but safe to leave)
var fmt2 = fmtCost;
function fmtInt(n) { return (n == null) ? '0' : Math.round(n).toLocaleString(); }

/* ─── sub-components ─── */
function KpiCard({ label, value, sub, green, loading }) {
  return (
    <div style={{
      background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 8, padding: '18px 20px',
    }}>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: '1.2px', color: '#71717a', marginBottom: 8,
      }}>{label}</div>
      {loading ? (
        <div style={{ height: 30, background: 'rgba(255,255,255,0.04)', borderRadius: 4, marginBottom: 4 }} />
      ) : (
        <div style={{
          fontSize: 26, fontWeight: 700, letterSpacing: '-1px', lineHeight: 1,
          marginBottom: 4, color: green ? '#22c55e' : '#fafafa',
        }}>{value}</div>
      )}
      {sub && <div style={{ fontSize: 11, color: '#71717a' }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, sub, children }) {
  return (
    <div style={{
      background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 8, padding: 20,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: '#71717a', marginBottom: 16 }}>{sub}</div>}
      {children}
    </div>
  );
}

function EmptyChart({ message }) {
  return (
    <div style={{
      height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 8,
      background: 'rgba(255,255,255,0.02)', borderRadius: 8,
      border: '1px dashed rgba(255,255,255,0.06)',
    }}>
      <div style={{ fontSize: 11, color: '#52525b', fontWeight: 500 }}>
        {message || 'No data yet'}
      </div>
      <div style={{ fontSize: 11, color: '#3f3f46', textAlign: 'center', maxWidth: 220 }}>
        Data will appear as your agents accumulate routing outcomes
      </div>
    </div>
  );
}

/* ─── main component ─── */
export default function Analytics() {
  const [months, setMonths] = useState(6);
  const costRef = useRef(null);
  const trafficRef = useRef(null);
  const [chartReady, setChartReady] = useState(false);

  const { data, isLoading, error } = useAnalyticsSummary(months);

  const { data: savingsData, isLoading: savingsLoading } = useQuery({
    queryKey: ['analyticsSavings'],
    queryFn: () => apiClient.getAnalyticsSavings(),
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  const { data: goalData, isLoading: goalLoading } = useQuery({
    queryKey: ['analyticsByGoal'],
    queryFn: () => apiClient.getAnalyticsByGoal(),
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  const { data: gate3Data, isLoading: gate3Loading } = useQuery({
    queryKey: ['gate3Retrospective'],
    queryFn: () => apiClient.getGate3Retrospective(),
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  // Load Chart.js once
  useEffect(function() {
    if (window.Chart) { setChartReady(true); return; }
    var s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
    s.onload = function() { setChartReady(true); };
    document.head.appendChild(s);
  }, []);

  // Render charts when data + Chart.js both ready
  useEffect(function() {
    if (!chartReady || !data) return;
    var Chart = window.Chart;

    // Destroy old
    ['_kCostChart', '_kTrafficChart'].forEach(function(k) {
      if (window[k]) { try { window[k].destroy(); } catch(e) {} window[k] = null; }
    });

    var history = data.cost_history || [];
    var traffic = data.traffic_by_model || [];

    // Cost bar chart
    if (costRef.current && history.length > 0) {
      window._kCostChart = new Chart(costRef.current, {
        type: 'bar',
        data: {
          labels: history.map(function(h) { return h.period; }),
          datasets: [{
            label: 'Spend ($)',
            data: history.map(function(h) { return h.cost_usd; }),
            backgroundColor: 'rgba(34,197,94,0.75)',
            borderRadius: 5,
          }],
        },
        options: {
          responsive: true,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#71717a' } },
            y: {
              grid: { color: 'rgba(255,255,255,0.04)' },
              ticks: { color: '#71717a', callback: function(v) { return '$' + v; } },
              beginAtZero: true,
            },
          },
        },
      });
    }

    // Traffic donut
    if (trafficRef.current && traffic.length > 0) {
      var COLORS = [
        'rgba(34,197,94,0.85)', 'rgba(59,130,246,0.85)', 'rgba(139,92,246,0.85)',
        'rgba(249,115,22,0.85)', 'rgba(20,184,166,0.85)', 'rgba(245,158,11,0.85)',
        'rgba(239,68,68,0.85)', 'rgba(168,85,247,0.85)',
      ];
      window._kTrafficChart = new Chart(trafficRef.current, {
        type: 'doughnut',
        data: {
          labels: traffic.map(function(t) { return t.model_id; }),
          datasets: [{
            data: traffic.map(function(t) { return t.count; }),
            backgroundColor: traffic.map(function(_, i) { return COLORS[i % COLORS.length]; }),
            borderWidth: 0,
          }],
        },
        options: {
          responsive: true,
          cutout: '65%',
          plugins: {
            legend: {
              position: 'bottom',
              labels: { color: '#a1a1aa', padding: 10, font: { size: 11 }, boxWidth: 10 },
            },
          },
        },
      });
    }
  }, [chartReady, data]);

  var RANGES = [
    { label: '1M', value: 1 },
    { label: '3M', value: 3 },
    { label: '6M', value: 6 },
    { label: '1Y', value: 12 },
  ];

  var hasHistory = data && data.cost_history && data.cost_history.length > 0;
  var hasTraffic = data && data.traffic_by_model && data.traffic_by_model.length > 0;

  return (
    <SidebarLayout>
      <div style={{ padding: '28px 32px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', marginBottom: 2 }}>Analytics</h1>
            <p style={{ fontSize: 13, color: '#71717a' }}>Cost, decisions, and routing traffic from your account</p>
          </div>
          <div style={{ display: 'flex', background: '#111113', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: 3 }}>
            {RANGES.map(function(r) {
              return (
                <button key={r.label} onClick={function() { setMonths(r.value); }} style={{
                  padding: '5px 12px', borderRadius: 5, fontSize: 12, fontWeight: 500,
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: months === r.value ? '#18181b' : 'transparent',
                  color: months === r.value ? '#fafafa' : '#71717a',
                }}>{r.label}</button>
              );
            })}
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
            borderRadius: 10, padding: '14px 18px', marginBottom: 24, fontSize: 13, color: '#fca5a5',
          }}>
            Failed to load analytics data. Please try refreshing.
          </div>
        )}

        {/* KPI row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          <KpiCard
            label="Spend this month"
            value={'$' + fmt2(data && data.current_period_cost)}
            sub="Total routing cost"
            green
            loading={isLoading}
          />
          <KpiCard
            label="Routing decisions"
            value={fmtInt(data && data.current_period_decisions)}
            sub="Decisions made this month"
            loading={isLoading}
          />
          <KpiCard
            label="Avg success rate"
            value={(data && data.avg_success_rate > 0) ? Math.round(data.avg_success_rate) + '%' : '—'}
            sub="Across all goals"
            green={data && data.avg_success_rate > 70}
            loading={isLoading}
          />
          <KpiCard
            label="Est. savings vs GPT-4o"
            value={savingsData && savingsData.savings_usd > 0 ? '$' + fmt2(savingsData.savings_usd) + ' saved' : '—'}
            sub="vs running everything through GPT-4o"
            green={savingsData && savingsData.savings_usd > 0}
            loading={savingsLoading}
          />
        </div>

        {/* Cost over time */}
        <div style={{ marginBottom: 16 }}>
          <ChartCard title="Cost over time" sub={'Monthly spend — last ' + months + ' months'}>
            {isLoading ? (
              <div style={{ height: 200, background: 'rgba(255,255,255,0.02)', borderRadius: 8 }} />
            ) : hasHistory ? (
              <div style={{ height: 200 }}><canvas ref={costRef} /></div>
            ) : (
              <EmptyChart message="No cost data yet" />
            )}
          </ChartCard>
        </div>

        {/* Traffic breakdown */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <ChartCard title="Traffic by model" sub="Where decisions routed (last 30 days)">
            {isLoading ? (
              <div style={{ height: 200, background: 'rgba(255,255,255,0.02)', borderRadius: 8 }} />
            ) : hasTraffic ? (
              <div style={{ height: 200 }}><canvas ref={trafficRef} /></div>
            ) : (
              <EmptyChart message="No traffic data yet" />
            )}
          </ChartCard>

          <ChartCard title="Monthly breakdown" sub="Decisions and traces per month">
            {isLoading ? (
              <div style={{ height: 200, background: 'rgba(255,255,255,0.02)', borderRadius: 8 }} />
            ) : hasHistory ? (
              <div style={{ overflowY: 'auto', maxHeight: 200 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['Month', 'Decisions', 'Traces', 'Cost'].map(function(h) {
                        return (
                          <th key={h} style={{
                            padding: '6px 10px', textAlign: h === 'Month' ? 'left' : 'right',
                            fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                            textTransform: 'uppercase', letterSpacing: '1px',
                            color: '#71717a', borderBottom: '1px solid rgba(255,255,255,0.06)',
                          }}>{h}</th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.cost_history || []).slice().reverse().map(function(row) {
                      return (
                        <tr key={row.period}>
                          <td style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", color: '#a1a1aa' }}>{row.period}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace' " }}>{fmtInt(row.decision_count)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace'" }}>{fmtInt(row.trace_count)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace'", color: '#22c55e' }}>${fmt2(row.cost_usd)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyChart message="No monthly data yet" />
            )}
          </ChartCard>
        </div>

        {/* Goal breakdown */}
        <div style={{ marginTop: 16 }}>
          <ChartCard title="Goal breakdown" sub="Success rate and routing per goal">
            {goalLoading ? (
              <div style={{ height: 160, background: 'rgba(255,255,255,0.02)', borderRadius: 8 }} />
            ) : goalData && goalData.goals && goalData.goals.length > 0 ? (
              <div style={{ overflowY: 'auto', maxHeight: 320 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['Goal', 'Dominant Model', 'Success Rate', 'Decisions', 'Avg Cost'].map(function(h) {
                        return (
                          <th key={h} style={{
                            padding: '6px 10px', textAlign: h === 'Goal' || h === 'Dominant Model' ? 'left' : 'right',
                            fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                            textTransform: 'uppercase', letterSpacing: '1px',
                            color: '#71717a', borderBottom: '1px solid rgba(255,255,255,0.06)',
                          }}>{h}</th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {goalData.goals.map(function(row) {
                      var srColor = row.success_rate == null ? '#71717a'
                        : row.success_rate > 80 ? '#22c55e'
                        : row.success_rate >= 60 ? '#eab308'
                        : '#ef4444';
                      return (
                        <tr key={row.goal}>
                          <td style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", color: '#a1a1aa' }}>{row.goal}</td>
                          <td style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", color: '#a1a1aa' }}>{row.dominant_model || '—'}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: srColor }}>
                            {row.success_rate != null ? row.success_rate + '%' : '—'}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace'" }}>{fmtInt(row.decision_count)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace'", color: '#22c55e' }}>
                            {row.avg_cost_usd != null ? '$' + fmt2(row.avg_cost_usd) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyChart message="No goal data yet" />
            )}
          </ChartCard>
        </div>

        {/* Gate 3 — Silence Signal Validation */}
        {!gate3Loading && gate3Data && gate3Data.data && gate3Data.data.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <ChartCard
              title="Gate 3 — Silence Signal Validation"
              sub="Checks whether positive silence interpretations were confirmed by downstream user actions"
            >
              <div style={{ overflowY: 'auto', maxHeight: 320 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['Goal', 'Model', 'Inferences', 'Confirmed', 'Contradicted', 'Confirmation Rate'].map(function(h) {
                        return (
                          <th key={h} style={{
                            padding: '6px 10px', textAlign: h === 'Goal' || h === 'Model' ? 'left' : 'right',
                            fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                            textTransform: 'uppercase', letterSpacing: '1px',
                            color: '#71717a', borderBottom: '1px solid rgba(255,255,255,0.06)',
                          }}>{h}</th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {gate3Data.data.map(function(row) {
                      var rate = row.confirmation_rate;
                      var rateColor = (rate == null || isNaN(rate)) ? '#71717a'
                        : rate >= 0.6 ? '#22c55e'
                        : rate >= 0.4 ? '#eab308'
                        : '#ef4444';
                      return (
                        <tr key={row.goal + ':' + row.model_id}>
                          <td style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", color: '#a1a1aa' }}>{row.goal}</td>
                          <td style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", color: '#a1a1aa' }}>{row.model_id}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace'" }}>{fmtInt(row.inference_count)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace'", color: '#22c55e' }}>{fmtInt(row.confirmed_positive)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace'", color: '#ef4444' }}>{fmtInt(row.contradicted)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: rateColor, fontWeight: 600 }}>
                            {(rate != null && !isNaN(rate)) ? (rate * 100).toFixed(1) + '%' : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 12, fontSize: 11, color: '#71717a', fontStyle: 'italic' }}>
                Tune silence_reward() thresholds when any goal type falls below 50%
              </div>
            </ChartCard>
          </div>
        )}

      </div>
    </SidebarLayout>
  );
}
